import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const Wrap = styled.section`
  padding: 5rem 0;

  @media (min-width: 900px) { padding: 7rem 0; }
`;

const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (min-width: 768px) { padding: 0 2rem; }
`;

const Head = styled.div`
  max-width: 720px;
  margin: 0 auto 3.5rem;
  text-align: center;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-family: var(--mono);
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 1.25rem;
  font-weight: 500;
`;

const Title = styled.h2`
  font-family: var(--sans);
  font-weight: 700;
  font-size: clamp(2.1rem, 4.6vw, 3.6rem);
  line-height: 1.0;
  letter-spacing: -0.032em;
  margin: 0;
  color: var(--ink);

  .accent {
    font-family: var(--serif);
    font-weight: 400;
    font-style: italic;
    letter-spacing: -0.012em;
    color: var(--accent);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 600px) {
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
  }
`;

const Card = styled(motion.article)`
  background: var(--bg-3);
  border: 1px solid var(--rule);
  border-radius: 16px;
  padding: 1.6rem 1.6rem 1.7rem;
  position: relative;
  box-shadow: var(--shadow-card);
  transition: border-color 280ms ease, box-shadow 320ms ease;

  @media (min-width: 600px) {
    &:last-child { grid-column: 1 / -1; }
  }

  &:hover {
    border-color: var(--rule-2);
    box-shadow: var(--shadow-card-hover);
  }

  .row {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    margin-bottom: 1rem;
  }

  .icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: var(--bg-2);
    border: 1px solid var(--rule);
    color: var(--ink);
    display: grid;
    place-items: center;
    flex-shrink: 0;

    svg { width: 18px; height: 18px; }
  }

  .num {
    font-family: var(--mono);
    font-size: 0.72rem;
    letter-spacing: 0.18em;
    color: var(--ink-3);
    font-weight: 500;
  }

  .title {
    font-family: var(--sans);
    font-weight: 600;
    font-size: 1.18rem;
    letter-spacing: -0.018em;
    color: var(--ink);
    margin: 0 0 0.55rem;
    line-height: 1.2;
  }

  .body {
    font-family: var(--sans);
    font-size: 0.97rem;
    line-height: 1.6;
    color: var(--ink-2);
    margin: 0;
  }
`;

const reveal = (i) => ({
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
});

const ICONS = {
  reactiva: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 12a8 8 0 11-3-6.2M20 4v5h-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  recuerda: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 8a6 6 0 1112 0c0 5 2 6 2 7H4c0-1 2-2 2-7zM10 19a2 2 0 004 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  recupera: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 4h2l2.5 11h11l2-8H7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="9"  cy="20" r="1.5" fill="currentColor" />
      <circle cx="17" cy="20" r="1.5" fill="currentColor" />
    </svg>
  ),
  recomienda: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2l2 4 4 1-3 3 1 4-4-2-4 2 1-4-3-3 4-1 2-4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M18 16l1 2 2 .5-1.5 1.5.5 2-2-1-2 1 .5-2L15 18.5l2-.5 1-2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  evalua: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3l2.6 5.3 5.9.85-4.27 4.16 1 5.86L12 16.8l-5.23 2.37 1-5.86L3.5 9.15l5.9-.85L12 3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  ),
};

const PILLARS = [
  {
    idx: "01",
    key: "reactiva",
    title: "Reactiva",
    body: "Reconecta con clientes que dejaron de comprarte. Una conversación oportuna antes que olviden tu marca.",
  },
  {
    idx: "02",
    key: "recuerda",
    title: "Recuerda",
    body: "Avisa a tu cliente justo cuando está por quedarse sin lo que ya te compra. Repone a tiempo, tú generas confianza.",
  },
  {
    idx: "03",
    key: "recupera",
    title: "Recupera",
    body: "Rescata carritos abandonados con un mensaje en el momento exacto. La venta que casi se pierde, vuelve.",
  },
  {
    idx: "04",
    key: "recomienda",
    title: "Recomienda",
    body: "Un agente de IA que conoce las relaciones entre todos los productos y las ofertas actuales en tu e-commerce — y le sugiere a cada cliente lo que de verdad le sirve.",
  },
  {
    idx: "05",
    key: "evalua",
    title: "Evalúa y crece",
    body: "Pide feedback tras cada compra e invita a quienes tuvieron buena experiencia a dejarte ★★★★★ en Google Reviews.",
  },
];

export default function Pillars() {
  return (
    <Wrap id="pilares">
      <Inner>
        <Head>
          <Eyebrow>Nuestros 5 pilares</Eyebrow>
          <Title>
            Lo que hacen tus<br />
            <span className="accent">agentes de IA.</span>
          </Title>
        </Head>

        <Grid>
          {PILLARS.map((p, i) => (
            <Card key={p.key} {...reveal(i)}>
              <div className="row">
                <div className="icon">{ICONS[p.key]}</div>
                <span className="num">{p.idx}</span>
              </div>
              <p className="title">{p.title}</p>
              <p className="body">{p.body}</p>
            </Card>
          ))}
        </Grid>
      </Inner>
    </Wrap>
  );
}
